/**
 * Error thrown when a hub call returns a non-2xx response. Carries the
 * structured fields from the hub's JSON error body so UI code (e.g. the
 * SchedulePreviewCard's error→copy switch) can render a useful message
 * instead of stringifying raw JSON.
 *
 * Two body shapes are produced by the hub today:
 *   - `{ code: '...' }` — schedule-draft routes (Plan 02-04) and other
 *     domain-typed routes use the stable enum directly as `code`.
 *   - `{ error: '...', message: '...' }` — generic Fastify routes set
 *     `error` to the stable code and `message` to a human detail.
 *
 * `code` is populated from whichever of `body.code` / `body.error` is a
 * non-empty string. Callers should read `.code` and switch on the value;
 * unknown codes fall back to whatever generic copy the UI defines.
 *
 * `cause` is set by `spec_fetch_failed` to the underlying system code
 * (`ECONNREFUSED`, `ENOTFOUND`, `UND_ERR_CONNECT_TIMEOUT`, `ABORT_ERR`, …).
 */
export class HubClientError extends Error {
    endpoint;
    httpStatus;
    statusText;
    code;
    cause;
    operationPath;
    field;
    violations;
    body;
    constructor(init) {
        const parts = [
            `${init.endpoint} failed:`,
            `${init.httpStatus} ${init.statusText}`,
        ];
        if (init.code)
            parts.push(`[${init.code}]`);
        if (init.detail)
            parts.push(init.detail);
        if (init.cause)
            parts.push(`(cause: ${init.cause})`);
        if (init.operationPath || init.field) {
            const ref = [init.operationPath, init.field].filter(Boolean).join(' / ');
            parts.push(`at ${ref}`);
        }
        super(parts.join(' '));
        this.name = 'HubClientError';
        this.endpoint = init.endpoint;
        this.httpStatus = init.httpStatus;
        this.statusText = init.statusText;
        this.code = init.code;
        this.cause = init.cause;
        this.operationPath = init.operationPath;
        this.field = init.field;
        this.violations = init.violations;
        this.body = init.body;
    }
}
/**
 * Parse a hub error response body. Accepts either `{ code }` (typed-route
 * shape) or `{ error, message }` (generic Fastify shape) and normalizes to
 * the fields HubClientError carries. Callers should pass the raw text body;
 * non-JSON bodies degrade gracefully to `{ body: undefined }`.
 */
export function parseHubErrorBody(text) {
    let body;
    try {
        body = text ? JSON.parse(text) : undefined;
    }
    catch {
        body = undefined;
    }
    const b = (body && typeof body === 'object' && !Array.isArray(body)
        ? body
        : {});
    // Both shapes: `code` (schedule + typed routes) and `error` (generic Fastify
    // routes) are the stable string identifier. First non-empty wins.
    const codeField = typeof b.code === 'string' && b.code.length > 0 ? b.code : undefined;
    const errorField = typeof b.error === 'string' && b.error.length > 0 ? b.error : undefined;
    const violations = Array.isArray(b.violations)
        ? b.violations
        : undefined;
    return {
        body,
        code: codeField ?? errorField,
        detail: typeof b.message === 'string' ? b.message : text ? text.slice(0, 200) : undefined,
        cause: typeof b.cause === 'string' ? b.cause : undefined,
        operationPath: typeof b.operationPath === 'string' ? b.operationPath : undefined,
        field: typeof b.field === 'string' ? b.field : undefined,
        violations,
    };
}
//# sourceMappingURL=errors.js.map